import type {
  ErrorCode,
  LobbyClientMessage,
  LobbyServerMessage,
  ReconnectFailReason,
  SeatedPlayer,
} from "@bn-games/network-protocol";
import type { ConnectionManager } from "./ConnectionManager.js";
import type { SessionStore } from "./SessionStore.js";

export type LobbyState =
  | "idle"
  | "connecting"
  | "creating"
  | "waiting_for_opponent"
  | "joining"
  | "ready"
  | "error";

export interface LobbyInfo {
  readonly state: LobbyState;
  readonly roomCode: string | null;
  /** Local player's seat index, set on GAME_START / RECONNECT_OK. */
  readonly localSeat: number | null;
  /** Total seats this room was created with. */
  readonly seatCount: number | null;
  /**
   * Roster of every seated peer (including the local player). Consumers derive
   * opponents via `players.filter(p => p.seat !== localSeat)`.
   */
  readonly players: readonly SeatedPlayer[] | null;
  readonly errorCode: ErrorCode | null;
  readonly errorMessage: string | null;
  /**
   * Optional per-game config declared by the host at room create — opaque to
   * this package, narrowed by each app at its boundary.
   */
  readonly variant: string | null;
  /**
   * Server-authoritative seat→role mapping carried on GAME_START /
   * RECONNECT_OK. Null until the server sends one (older servers, or pre-game
   * lobby state). Apps that map seat→color (backgammon, checkers) should
   * prefer `seats[localSeat]` over a static SEATS constant, since seats may
   * have rotated due to `preferredSeat` or a rematch.
   */
  readonly seats: readonly string[] | null;
  /**
   * True iff the match is in verifiable mode. Set on ROOM_JOINED (host's
   * preference echoed to joiner), GAME_START, REMATCH_START, RESTART_START,
   * and RECONNECT_OK. Apps that record per-turn proofs read this to decide
   * whether to send REQUEST_ROLL or roll locally.
   */
  readonly verifiable: boolean;
  /**
   * drand chain hash the match was started with, present on GAME_START /
   * RECONNECT_OK when `verifiable` is true. Recorded into history so a
   * verifier modal years later can still resolve the right beacon archive.
   */
  readonly drandChainHash: string | null;
}

export type LobbyListener = (info: LobbyInfo) => void;
export type ReconnectFailListener = (reason: ReconnectFailReason) => void;

/**
 * Room settings for `joinOrCreate`. Every field but `playerName` is honoured
 * only when this client is the one that creates the room — a later joiner's
 * preferences never re-configure a table that already exists. An options bag
 * rather than positional parameters: the create-side knobs are all optional
 * and several share a type, so call sites would otherwise be a run of
 * indistinguishable `undefined`s.
 */
export interface JoinOrCreateOptions {
  readonly playerName: string;
  readonly variant?: string;
  readonly preferredSeat?: number;
  readonly seatCount?: number;
  readonly verifiable?: boolean;
  /** Opaque per-game config, mirrored from `createRoom`. */
  readonly options?: Record<string, unknown>;
}

/**
 * Drives the lobby state machine (idle → connecting → creating/joining →
 * waiting_for_opponent → ready) and the mid-game reconnect handshake. Game
 * agnostic — the only `Connection` payload it sends/receives is the shared
 * lobby envelope. Game-specific gameplay messages flow past it directly to
 * the per-app NetworkGameController.
 */
export class RoomClient<
  TClient = LobbyClientMessage,
  TServer = LobbyServerMessage,
> {
  private readonly connection: ConnectionManager<TClient, TServer>;
  private readonly sessionStore: SessionStore;
  private readonly listeners = new Set<LobbyListener>();
  private readonly reconnectFailListeners = new Set<ReconnectFailListener>();
  private unsubMessage: (() => void) | null = null;
  private unsubState: (() => void) | null = null;

  private lobbyState: LobbyState = "idle";
  private roomCode: string | null = null;
  private localSeat: number | null = null;
  private seatCount: number | null = null;
  private players: readonly SeatedPlayer[] | null = null;
  private errorCode: ErrorCode | null = null;
  private errorMessage: string | null = null;
  private pendingAction: (() => void) | null = null;
  private sessionToken: string | null = null;
  /**
   * The name last used to create/join/resume, so a mid-game socket reopen
   * with no stored token (an identity-only reclaim never receives one) can
   * still fall back to RESUME_ROOM instead of silently giving up.
   */
  private playerName: string | null = null;
  private variant: string | null = null;
  private seats: readonly string[] | null = null;
  private verifiable = false;
  private drandChainHash: string | null = null;

  constructor(
    connection: ConnectionManager<TClient, TServer>,
    sessionStore: SessionStore,
  ) {
    this.connection = connection;
    this.sessionStore = sessionStore;
  }

  getInfo(): LobbyInfo {
    return {
      state: this.lobbyState,
      roomCode: this.roomCode,
      localSeat: this.localSeat,
      seatCount: this.seatCount,
      players: this.players,
      errorCode: this.errorCode,
      errorMessage: this.errorMessage,
      variant: this.variant,
      seats: this.seats,
      verifiable: this.verifiable,
      drandChainHash: this.drandChainHash,
    };
  }

  onUpdate(listener: LobbyListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onReconnectFail(listener: ReconnectFailListener): () => void {
    this.reconnectFailListeners.add(listener);
    return () => this.reconnectFailListeners.delete(listener);
  }

  getSessionToken(): string | null {
    return this.sessionToken;
  }

  getConnection(): ConnectionManager<TClient, TServer> {
    return this.connection;
  }

  reconnect(roomCode: string, sessionToken: string): void {
    this.reset();
    this.setRoomCode(roomCode.toUpperCase());
    this.sessionToken = sessionToken;
    this.lobbyState = "connecting";
    this.notify();

    this.subscribe();
    this.pendingAction = (): void => {
      const code = this.roomCode;
      const tok = this.sessionToken;
      if (code === null || tok === null) return;
      this.sendLobby({ type: "RECONNECT_ROOM", roomCode: code, sessionToken: tok });
    };
    this.connection.connect();
  }

  /**
   * Server-resolved resume: the server decides whether `roomCode` reclaims a
   * held seat (by identity or by the stored token) or seats a new player.
   * Both success replies (`ROOM_JOINED`, `RECONNECT_OK`) are already handled
   * by `handleMessage` — this only needs to send the request and, when a
   * token is on hand, stash it on `this.sessionToken` so a `RECONNECT_OK`
   * reply re-persists it with a refreshed TTL via the existing save guard.
   */
  resume(roomCode: string, playerName: string): void {
    this.reset();
    this.setRoomCode(roomCode.toUpperCase());
    this.lobbyState = "connecting";
    this.notify();

    this.subscribe();
    this.pendingAction = (): void => {
      const code = this.roomCode;
      if (code === null) return;
      this.lobbyState = "joining";
      this.notify();
      this.playerName = playerName;
      this.sessionToken = this.sessionStore.loadFor(code)?.sessionToken ?? null;
      const token = this.sessionToken;
      this.sendLobby({
        type: "RESUME_ROOM",
        roomCode: code,
        playerName,
        ...(token !== null ? { sessionToken: token } : {}),
      });
    };
    this.connection.connect();
  }

  createRoom(
    playerName: string,
    variant?: string,
    preferredSeat?: number,
    seatCount?: number,
    verifiable?: boolean,
    options?: Record<string, unknown>,
  ): void {
    this.reset();
    this.variant = variant ?? null;
    this.verifiable = verifiable === true;
    this.lobbyState = "connecting";
    this.notify();

    this.subscribe();
    this.pendingAction = (): void => {
      this.lobbyState = "creating";
      this.notify();
      this.playerName = playerName;
      const message: LobbyClientMessage = {
        type: "CREATE_ROOM",
        playerName,
        ...(variant !== undefined ? { variant } : {}),
        ...(typeof preferredSeat === "number" ? { preferredSeat } : {}),
        ...(typeof seatCount === "number" ? { seatCount } : {}),
        ...(verifiable === true ? { verifiable: true } : {}),
        ...(options !== undefined ? { options } : {}),
      };
      this.sendLobby(message);
    };
    this.connection.connect();
  }

  /**
   * Meet everyone sharing `externalKey` at one table, creating the room if this
   * client gets there first. The room code is unknown until the reply arrives,
   * so — unlike `joinRoom` — nothing is assumed about it up front. The lobby
   * reports "joining" while the reply is outstanding: create vs. join is
   * ambiguous until the server resolves it, and joining is the outcome every
   * client but the first one sees.
   */
  joinOrCreate(externalKey: string, opts: JoinOrCreateOptions): void {
    const { playerName, variant, preferredSeat, seatCount, verifiable, options } =
      opts;
    this.reset();
    this.variant = variant ?? null;
    this.verifiable = verifiable === true;
    this.lobbyState = "connecting";
    this.notify();

    this.subscribe();
    this.pendingAction = (): void => {
      this.lobbyState = "joining";
      this.notify();
      this.playerName = playerName;
      const message: LobbyClientMessage = {
        type: "JOIN_OR_CREATE",
        externalKey,
        playerName,
        ...(variant !== undefined ? { variant } : {}),
        ...(typeof preferredSeat === "number" ? { preferredSeat } : {}),
        ...(typeof seatCount === "number" ? { seatCount } : {}),
        ...(verifiable === true ? { verifiable: true } : {}),
        ...(options !== undefined ? { options } : {}),
      };
      this.sendLobby(message);
    };
    this.connection.connect();
  }

  joinRoom(roomCode: string, playerName: string): void {
    this.reset();
    this.lobbyState = "connecting";
    this.setRoomCode(roomCode.toUpperCase());
    this.notify();

    this.subscribe();
    this.pendingAction = (): void => {
      const code = this.roomCode;
      if (code === null) return;
      this.lobbyState = "joining";
      this.notify();
      this.playerName = playerName;
      this.sendLobby({ type: "JOIN_ROOM", roomCode: code, playerName });
    };
    this.connection.connect();
  }

  cancel(): void {
    this.unsubscribe();
    this.connection.disconnect();
    // Room-scoped: abandoning this attempt must not wipe a session this
    // client still holds for a different room.
    if (this.roomCode !== null) this.sessionStore.clearFor(this.roomCode);
    this.reset();
    this.notify();
  }

  private sendLobby(message: LobbyClientMessage): void {
    // All lobby messages are part of the app's TClient union by construction.
    this.connection.send(message as unknown as TClient);
  }

  private subscribe(): void {
    this.unsubscribe();

    this.unsubState = this.connection.onStateChange((state) => {
      if (state === "connected" && this.pendingAction) {
        const action = this.pendingAction;
        this.pendingAction = null;
        action();
      } else if (state === "connected" && this.lobbyState === "ready" && this.roomCode) {
        if (this.sessionToken) {
          // Mid-game socket reopen — auto-rebind to our slot.
          this.sendLobby({
            type: "RECONNECT_ROOM",
            roomCode: this.roomCode,
            sessionToken: this.sessionToken,
          });
        } else if (this.playerName) {
          // No stored token — an identity-only reclaim never receives one.
          // resume() can still find the seat by verified identity alone.
          this.sendLobby({
            type: "RESUME_ROOM",
            roomCode: this.roomCode,
            playerName: this.playerName,
          });
        }
      } else if (
        state === "disconnected" &&
        this.lobbyState !== "idle" &&
        this.lobbyState !== "ready"
      ) {
        this.lobbyState = "error";
        this.errorMessage = "Connection lost";
        this.notify();
      }
    });

    this.unsubMessage = this.connection.onMessage((msg) => {
      this.handleMessage(msg);
    });
  }

  private unsubscribe(): void {
    if (this.unsubMessage) {
      this.unsubMessage();
      this.unsubMessage = null;
    }
    if (this.unsubState) {
      this.unsubState();
      this.unsubState = null;
    }
  }

  private handleMessage(serverMsg: TServer): void {
    // Game-specific messages flow past — they fall through the switch since
    // their `type` doesn't match any lobby case.
    const msg = serverMsg as LobbyServerMessage;
    switch (msg.type) {
      case "ROOM_CREATED":
        this.setRoomCode(msg.roomCode);
        this.sessionToken = msg.sessionToken;
        this.sessionStore.save(msg.roomCode, msg.sessionToken);
        this.lobbyState = "waiting_for_opponent";
        this.notify();
        break;

      case "ROOM_JOINED":
        this.setRoomCode(msg.roomCode);
        this.sessionToken = msg.sessionToken;
        this.localSeat = msg.seat;
        this.seatCount = msg.seatCount;
        this.players = msg.players;
        if (msg.variant !== undefined) this.variant = msg.variant;
        // Joiner inherits the host's verifiable preference. Local settings
        // toggle is advisory only — the wire value wins for this room.
        this.verifiable = msg.verifiable === true;
        this.sessionStore.save(msg.roomCode, msg.sessionToken);
        this.notify();
        // Don't set ready yet — wait for GAME_START.
        break;

      case "OPPONENT_JOINED":
        // 2-seat shorthand: the joiner is the only other peer. For N>2, the
        // authoritative roster reaches us on GAME_START (or PLAYER_JOINED,
        // once introduced) — main.ts shouldn't lean on this in those games.
        if (this.players && this.players.length === 1 && this.seatCount === 2) {
          const localSeat = this.players[0]?.seat ?? 0;
          this.players = [
            ...this.players,
            msg.player ?? { seat: 1 - localSeat, name: msg.opponentName },
          ];
        }
        break;

      case "GAME_START":
        this.localSeat = msg.seat;
        this.seatCount = msg.seatCount;
        this.players = msg.players;
        if (msg.variant !== undefined) this.variant = msg.variant;
        if (msg.seats !== undefined) this.seats = msg.seats;
        this.verifiable = msg.verifiable === true;
        this.drandChainHash = msg.drandChainHash ?? null;
        this.lobbyState = "ready";
        this.notify();
        break;

      case "RECONNECT_OK":
        this.setRoomCode(msg.roomCode);
        this.localSeat = msg.seat;
        this.seatCount = msg.seatCount;
        this.players = msg.players;
        if (msg.variant !== undefined) this.variant = msg.variant;
        if (msg.seats !== undefined) this.seats = msg.seats;
        this.verifiable = msg.verifiable === true;
        this.drandChainHash = msg.drandChainHash ?? null;
        if (this.roomCode && this.sessionToken) {
          this.sessionStore.save(this.roomCode, this.sessionToken);
        }
        this.lobbyState = "ready";
        this.notify();
        break;

      case "RECONNECT_FAIL":
        this.sessionToken = null;
        // Room-scoped: a displaced/expired seat in this room must not wipe
        // tokens for other rooms this client still holds.
        if (this.roomCode !== null) this.sessionStore.clearFor(this.roomCode);
        // Only reset cold-start (pre-game) attempts to idle. Mid-game failures
        // leave lobbyState === "ready" so the network overlay can take over.
        if (this.lobbyState !== "ready") {
          this.lobbyState = "idle";
          this.setRoomCode(null);
          this.notify();
        }
        for (const listener of this.reconnectFailListeners) listener(msg.reason);
        break;

      case "ERROR":
        this.lobbyState = "error";
        this.errorCode = msg.code;
        this.errorMessage = msg.message;
        this.notify();
        break;

      default:
        // Game-specific message — ignore at the lobby layer.
        break;
    }
  }

  private reset(): void {
    this.lobbyState = "idle";
    this.setRoomCode(null);
    this.localSeat = null;
    this.seatCount = null;
    this.players = null;
    this.errorCode = null;
    this.errorMessage = null;
    this.pendingAction = null;
    this.sessionToken = null;
    this.playerName = null;
    this.variant = null;
    this.seats = null;
    this.verifiable = false;
    this.drandChainHash = null;
  }

  /**
   * Keeps the connection's `?room=` in step with the code this client is
   * bound to, so the socket carrying a join/resume — and every automatic
   * reconnect after a drop, including once ROOM_CREATED names a fresh room —
   * addresses that room.
   */
  private setRoomCode(code: string | null): void {
    this.roomCode = code;
    this.connection.setRoom(code);
  }

  private notify(): void {
    const info = this.getInfo();
    for (const listener of this.listeners) listener(info);
  }
}
